import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bell, ChevronRight, X } from 'lucide-react'

// Persistent reminder banner that sits at the top of the home page
// whenever notifications aren't enabled. Two callsites cause it to
// surface:
//   - the user dismissed the prompt-on-open modal without granting
//   - browser permission landed on 'denied' (the modal is gone, but
//     they can still re-enable from browser settings via /settings)
//
// Tap on the banner → /settings → reminders section. Dismiss button
// hides it for ~3 days (handled by useNotificationPrompt) so we don't
// nag every cold-launch but they get nudged again later.
export default function NotificationBanner({ permission, onDismiss }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const body = permission === 'denied'
    ? t('notifications.bannerBodyBlocked')
    : t('notifications.bannerBody')

  return (
    <div
      className="flex items-center gap-2 rounded-md border-2 border-app-primary-500/40 bg-app-primary-500/10 px-3 py-2.5 mb-3"
      dir="auto"
    >
      <button
        type="button"
        onClick={() => navigate('/settings')}
        className="flex-1 flex items-center gap-2 min-w-0 text-start"
      >
        <span className="p-1.5 rounded-md bg-app-primary-500/20 text-app-primary-500 flex-shrink-0">
          <Bell className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-foreground truncate">
            {t('notifications.bannerTitle')}
          </span>
          <span className="block text-[11px] text-muted-foreground truncate">{body}</span>
        </span>
        <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('settings.close')}
        className="p-1 rounded-md text-muted-foreground hover:text-foreground flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
